<template>
  <v-card class="pa-4" max-width="1200">
    <v-card-title class="text-subtitle-1 font-weight-bold">
      Editable Users (Validated)
      <v-spacer />
      <v-btn color="primary" small @click="addUser">
        <v-icon left small>mdi-plus</v-icon> Add New Line
      </v-btn>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="users"
      :items-per-page="5"
      :page.sync="currentPage"
      class="elevation-1 compact-edit"
    >
      <!-- Name -->
      <template v-slot:item.name="{ item }">
        <div v-if="!item.isEditing" class="py-1 cell-text">{{ item.name }}</div>
        <v-text-field
          v-else
          v-model="item.editBuffer.name"
          :rules="nameRules"
          dense
          hide-details="auto"
          outlined
          class="compact-field"
        />
      </template>

      <!-- Email -->
      <template v-slot:item.email="{ item }">
        <div v-if="!item.isEditing" class="py-1 cell-text">{{ item.email }}</div>
        <v-text-field
          v-else
          v-model="item.editBuffer.email"
          :rules="emailRules"
          dense
          hide-details="auto"
          outlined
          class="compact-field"
        />
      </template>

      <!-- Age -->
      <template v-slot:item.age="{ item }">
        <div v-if="!item.isEditing" class="py-1 cell-text">{{ item.age }}</div>
        <v-text-field
          v-else
          v-model.number="item.editBuffer.age"
          type="number"
          :rules="ageRules"
          dense
          hide-details="auto"
          outlined
          class="compact-field"
        />
      </template>

      <!-- Role -->
      <template v-slot:item.role="{ item }">
        <div v-if="!item.isEditing" class="py-1">
          <v-chip :color="getRoleColor(item.role)" dark class="chip-text">{{ item.role }}</v-chip>
        </div>
        <v-select
          v-else
          v-model="item.editBuffer.role"
          :items="roleOptions"
          dense
          hide-details="auto"
          outlined
          class="compact-field"
        />
      </template>

      <!-- Details (Two rows) -->
      <template v-slot:item.details="{ item }">
        <div v-if="!item.isEditing" class="py-1">
          <div class="detail-title">{{ item.detailTitle }}</div>
          <div class="detail-sub">{{ item.detailSub }}</div>
        </div>
        <div v-else>
          <v-text-field
            v-model="item.editBuffer.detailTitle"
            dense
            hide-details="auto"
            outlined
            placeholder="Title"
            class="compact-field two-line-top"
          />
          <v-text-field
            v-model="item.editBuffer.detailSub"
            dense
            hide-details="auto"
            outlined
            placeholder="Subtitle"
            class="compact-field two-line-bottom"
          />
        </div>
      </template>

      <!-- Valid User -->
      <template v-slot:item.isValid="{ item }">
        <div v-if="!item.isEditing" class="text-center py-1">
          <v-icon v-if="item.isValid" color="success">mdi-check-circle</v-icon>
          <v-icon v-else color="grey lighten-1">mdi-close-circle</v-icon>
        </div>
        <div v-else class="text-center">
          <v-checkbox
            v-model="item.editBuffer.isValid"
            dense
            hide-details
            class="ma-0 pa-0 d-inline-block"
          />
        </div>
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <div v-if="!item.isEditing" class="d-flex align-center">
          <v-btn icon small color="primary" @click="startEdit(item)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small color="error" class="ml-3" @click="confirmDelete(item)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
        <div v-else class="d-flex align-center">
          <v-btn icon small color="success" @click="saveEdit(item)">
            <v-icon>mdi-check</v-icon>
          </v-btn>
          <v-btn icon small color="grey" class="ml-3" @click="cancelEdit(item)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete <strong>{{ itemToDelete?.name }}</strong>?
          <br />This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" text @click="executeDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-divider class="my-4" />
    <v-card-subtitle class="subtitle-text">Saved Data (first 3)</v-card-subtitle>
    <v-card-text>
      <pre class="pre-text">{{ savedUsers.slice(0, 3) }}</pre>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  data() {
    return {
      headers: [
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Email', value: 'email', sortable: true },
        { text: 'Age', value: 'age', sortable: true, width: '90px' },
        { text: 'Role', value: 'role', sortable: true, width: '140px' },
        { text: 'Details', value: 'details', sortable: false, width: '200px' },
        { text: 'Valid', value: 'isValid', sortable: true, align: 'center', width: '80px' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '120px' }
      ],
      users: [],
      currentPage: 1,
      nextId: 1,
      deleteDialog: false,
      itemToDelete: null,

      roleOptions: [
        'Administrator', 'Manager', 'Developer', 'Designer', 'Tester',
        'Analyst', 'Support', 'Sales', 'HR', 'Guest'
      ],

      nameRules: [v => !!v || 'Required'],
      emailRules: [
        v => !!v || 'Required',
        v => /.+@.+\..+/.test(v) || 'Invalid email'
      ],
      ageRules: [
        v => v !== '' && v !== null || 'Required',
        v => Number.isInteger(Number(v)) || 'Whole number',
        v => v >= 10 || 'Min 10',
        v => v <= 120 || 'Max 120'
      ]
    };
  },
  computed: {
    savedUsers() {
      return this.users
        .filter(u => !u.isEditing)
        .map(({ id, name, email, age, role, detailTitle, detailSub, isValid }) => ({
          id, name, email, age, role, detailTitle, detailSub, isValid
        }));
    }
  },
  created() {
    for (let i = 1; i <= 50; i++) {
      this.users.push({
        id: i,
        name: `User ${i}`,
        email: `user${i}@example.com`,
        age: 20 + (i % 40),
        role: this.roleOptions[i % 10],
        detailTitle: `Project ${String.fromCharCode(65 + (i % 26))}`,
        detailSub: `Task group ${(i % 5) + 1}`,
        isValid: i % 3 === 0,
        isEditing: false
      });
    }
    this.nextId = 51;
  },
  methods: {
    getRoleColor(role) {
      const colors = {
        Administrator: 'red', Manager: 'purple', Developer: 'blue',
        Designer: 'pink', Tester: 'orange', Analyst: 'teal',
        Support: 'green', Sales: 'indigo', HR: 'cyan', Guest: 'grey'
      };
      return colors[role] || 'grey';
    },

    addUser() {
      this.users.unshift({
        id: this.nextId++,
        name: '',
        email: '',
        age: null,
        role: 'Guest',
        detailTitle: '',
        detailSub: '',
        isValid: false,
        isEditing: true,
        editBuffer: {
          name: '', email: '', age: null, role: 'Guest',
          detailTitle: '', detailSub: '', isValid: false
        }
      });
      this.currentPage = 1;
    },

    startEdit(item) {
      item.editBuffer = {
        name: item.name,
        email: item.email,
        age: item.age,
        role: item.role,
        detailTitle: item.detailTitle,
        detailSub: item.detailSub,
        isValid: item.isValid
      };
      item.isEditing = true;
    },

    saveEdit(item) {
      const nameValid = this.nameRules.every(r => r(item.editBuffer.name) === true);
      const emailValid = this.emailRules.every(r => r(item.editBuffer.email) === true);
      const ageValid = this.ageRules.every(r => r(item.editBuffer.age) === true);

      if (!nameValid || !emailValid || !ageValid) return;

      item.name = item.editBuffer.name;
      item.email = item.editBuffer.email;
      item.age = item.editBuffer.age;
      item.role = item.editBuffer.role;
      item.detailTitle = item.editBuffer.detailTitle;
      item.detailSub = item.editBuffer.detailSub;
      item.isValid = item.editBuffer.isValid;
      item.isEditing = false;
      this.$delete(item, 'editBuffer');
    },

    cancelEdit(item) {
      const isEmpty = !item.name && !item.email && (item.age === null || item.age === 0);
      if (isEmpty) {
        this.removeUser(item);
      } else {
        item.isEditing = false;
        this.$delete(item, 'editBuffer');
      }
    },

    // --- Safe delete flow ---
    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    executeDelete() {
      if (this.itemToDelete) {
        this.removeUser(this.itemToDelete);
      }
      this.deleteDialog = false;
      this.itemToDelete = null;
    },

    removeUser(item) {
      const index = this.users.findIndex(u => u.id === item.id);
      if (index !== -1) this.users.splice(index, 1);
    }
  }
};
</script>

<style scoped>
/* === View mode text === */
.cell-text {
  font-size: 16px;
}

/* === Chip text === */
.chip-text {
  font-size: 14px !important;
  height: 28px !important;
}

/* === Two-line details === */
.detail-title {
  font-size: 16px;
  font-weight: bold;
  line-height: 1.3;
}
.detail-sub {
  font-size: 14px;
  color: #9e9e9e;
  line-height: 1.3;
}

/* === Edit mode inputs === */
.compact-field {
  margin-top: 1px !important;
  margin-bottom: 1px !important;
  font-size: 15px !important;
}
.compact-field >>> .v-input__control {
  min-height: 34px !important;
}
.compact-field >>> .v-input__slot {
  min-height: 34px !important;
  padding-left: 8px !important;
  padding-right: 8px !important;
}
.compact-field >>> input,
.compact-field >>> .v-select__selection {
  font-size: 15px !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

/* === Two-line stacked fields === */
.two-line-top {
  margin-bottom: 2px !important;
}
.two-line-bottom {
  margin-top: 0 !important;
}

/* === Table row height === */
.compact-edit >>> td {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  height: auto !important;
  vertical-align: middle;
}

/* === Spacing between action buttons === */
.compact-edit .v-btn + .v-btn {
  margin-left: 12px;
}

/* === Card subtitle & pre block === */
.subtitle-text {
  font-size: 16px;
}
.pre-text {
  font-size: 15px;
}
</style>